import React, { ReactNode, useMemo } from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { Investment } from "@rentablo/api/apiv1";
import { useCurrencyDisplay } from "@providers/CurrencyDisplayProvider";
import { sortInvestments } from "./investmentListFields";
import { InvestmentListView } from "./useInvestmentListView";
import { InvestmentMobileRow } from "./InvestmentMobileRow";

// One sideways scroller for all rows: a swipe anywhere on the list moves the
// chips of every row together, while each row's top part stays put (sticky).
// The container query lets that top part size itself to the visible width.
const RowsScroller = styled.div({
  overflowX: "auto",
  overflowY: "hidden",
  overscrollBehaviorX: "contain",
  containerType: "inline-size",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
});

// As wide as the widest row, so every row and its divider span the full
// scroll width.
const Rows = styled.div(({ theme }) => ({
  width: "max-content",
  minWidth: "100%",
  // Closes the first row on top like the dividers between rows.
  "&:not(:empty)": {
    borderTop: `1px solid ${theme.palette.border.secondary}`,
  },
}));

interface Props {
  investments: Investment[];
  images: Record<string, string | null | undefined>;
  view: InvestmentListView;
  className?: string;
  isFetching?: boolean;
  onRowClick?: (investment: Investment) => void;
  onNameClick?: (investmentId: string) => void;
  onAddQuote?: (investment: Investment) => void;
  // An action for the whole list in the header line, e.g. Export.
  headerAction?: ReactNode;
  // At the end of the header line, shown even when the list is empty.
  settingsAction?: ReactNode;
}

export const InvestmentMobileList = ({
  investments,
  images,
  view,
  className,
  isFetching,
  onRowClick,
  onNameClick,
  onAddQuote,
  headerAction,
  settingsAction,
}: Props) => {
  const { t } = useTranslation();
  const { selectedCurrencyView } = useCurrencyDisplay();
  const { sortField, sortDirection } = view;
  const hasEntries = investments.length > 0;

  const sortedInvestments = useMemo(
    () =>
      sortInvestments(
        investments,
        sortField,
        sortDirection,
        selectedCurrencyView === "global"
      ),
    [investments, sortField, sortDirection, selectedCurrencyView]
  );

  return (
    <Box
      className={className}
      data-testid="investment-mobile-list"
      sx={{ opacity: isFetching ? 0.6 : 1 }}
    >
      {(hasEntries || settingsAction) && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={3}
          mb={3}
          data-testid="investment-mobile-list-header"
        >
          <Typography variant="body3" color="textSecondary" noWrap>
            {hasEntries &&
              t("domains.investments.investmentTable.entries", {
                count: investments.length,
              })}
          </Typography>
          <Box display="flex" alignItems="center" gap={2} flex="none">
            {hasEntries && headerAction}
            {settingsAction}
          </Box>
        </Box>
      )}
      <RowsScroller data-testid="investment-mobile-rows">
        <Rows>
          {sortedInvestments.map((investment) => (
            <InvestmentMobileRow
              key={investment.id}
              investment={investment}
              image={
                images[investment.isin || ""] ||
                images[investment.tickerSymbol || ""]
              }
              metricOrder={view.metricOrder}
              showLabels={view.showLabels}
              onRowClick={onRowClick}
              onNameClick={onNameClick}
              onAddQuote={onAddQuote}
            />
          ))}
        </Rows>
      </RowsScroller>
    </Box>
  );
};
